import { v4 } from "uuid";

import ExportablesTemplateHeaderSection from "@/components/AIComponents/ExportablesModal/templates/components/ExportablesTemplateHeaderSection";
import OrganizerDirections from "@/components/AIComponents/ExportablesModal/templates/components/OrganizerDirections";
import { TemplateProps } from "@/components/AIComponents/ExportablesModal/templates/registry";
import { AdjustFontSizeToFitContent } from "@/view/utils/useAdjustFontSizeToFitContent/useAdjustFontSizeToFitContent";
import * as styles from "./ContextCluesDetective.styles";

export interface ContextCluesFormData {
  article_title?: string;
  directions?: string;
  excerpts?: Array<{
    text?: string;
    bold_ranges?: Array<{
      bold_word?: string;
      start?: number;
      end?: number;
    }>;
    formatted_text?: string | undefined;
    contextClues?: string | undefined;
    definition?: string | undefined;
  }>;
}

const defaultExcerpts = [
  {
    text: "AI to pull sentence with a Tier 2 or 3 word that contains context clues.",
    formatted_text:
      "AI to pull sentence with a Tier 2 or 3 <strong><u>word</u></strong> that contains context clues.",
    targetWord: "Underline word.",
    contextClues: "",
    definition: "",
  },
  {
    text: "AI to pull sentence with a Tier 2 or 3 word that contains context clues.",
    formatted_text:
      "AI to pull sentence with a Tier 2 or 3 <strong><u>word</u></strong> that contains context clues.",
    targetWord: "Underline word.",
    contextClues: "",
    definition: "",
  },
];

// Type guard to check if formData is ContextCluesFormData
const isContextCluesFormData = (data: any): data is ContextCluesFormData => {
  return (
    data &&
    typeof data === "object" &&
    ("article_title" in data || "directions" in data || "excerpts" in data)
  );
};

const ContextCluesDetective = ({
  formData,
  onChange,
  isStudent = false,
}: TemplateProps<ContextCluesFormData>) => {
  // Ensure formData is treated as ContextCluesFormData
  const contextCluesData = isContextCluesFormData(formData)
    ? formData
    : ({} as ContextCluesFormData);

  const directionsArticleTitleText = contextCluesData?.article_title
    ? `Read the excerpts from the article, "${contextCluesData?.article_title}".`
    : "Read the excerpts from this article.";
  const directionsText = `${directionsArticleTitleText} Use context clues in the excerpt to determine the meaning of the underlined words in each excerpt. Then use the context clues to help you write a definition of the underlined word.`;

  const { directions = directionsText } = contextCluesData;

  const formattedExcerpts = contextCluesData?.excerpts?.map((excerpt) => {
    let updatedText = excerpt?.text;
    const start = excerpt?.bold_ranges?.[0]?.start;
    const end = excerpt?.bold_ranges?.[0]?.end;
    if (start && end) {
      updatedText =
        excerpt?.text?.slice(0, start) +
        "<strong><u>" +
        excerpt?.text?.slice(start, end) +
        "</u></strong>" +
        excerpt?.text?.slice(end);
    }
    return {
      ...excerpt,
      formatted_text: "<strong>Excerpt: </strong>" + updatedText,
    };
  });

  // Determine which excerpts to use, prioritizing user-provided excerpts
  const excerptData = formattedExcerpts || defaultExcerpts;

  const handleDirectionsChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    onChange?.({
      ...contextCluesData,
      directions: e.target.value ?? directionsText,
    });
  };

  const handleExcerptChange = (index: number, field: string, value: string) => {
    const updatedExcerpts = [
      ...(contextCluesData?.excerpts || defaultExcerpts),
    ];
    updatedExcerpts[index] = {
      ...updatedExcerpts[index],
      [field]: value,
    };

    onChange?.({
      ...contextCluesData,
      excerpts: updatedExcerpts,
    });
  };

  return (
    <div css={styles.containerStyles}>
      <ExportablesTemplateHeaderSection
        templateTitle="Context clues worksheet"
        articleTitle={contextCluesData?.article_title}
        directions={
          <OrganizerDirections
            name="Content Clues"
            value={contextCluesData?.directions || directions}
            handleChange={handleDirectionsChange}
            isStudent={isStudent}
          />
        }
      />

      {excerptData.slice(0, 2).map((excerpt, index) => (
        <div key={index}>
          <div css={styles.excerptContainer}>
            <p
              css={styles.excerptStyles}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: excerpt.formatted_text }}
            />
          </div>
          <div css={styles.inputContainer}>
            <span css={styles.inputLabelStyles}>Context clues: </span>
            <AdjustFontSizeToFitContent deps={[excerpt.contextClues]}>
              <textarea
                id={`context-clues-excerpt-cc-input-${index + 1}-${v4()}`}
                data-qa-selector={`exportables-context-clues-excerpt-cc-input-${index + 1}`}
                aria-label="Context clues input"
                css={styles.textareaStyles}
                value={excerpt.contextClues}
                onChange={(e) =>
                  handleExcerptChange(index, "contextClues", e.target.value)
                }
                maxLength={350}
              />
            </AdjustFontSizeToFitContent>
          </div>
          <div css={styles.inputContainer}>
            <span css={styles.inputLabelStyles}>Your definition:</span>
            <AdjustFontSizeToFitContent deps={[excerpt.definition]}>
              <textarea
                id={`context-clues-excerpt-definition-input-${index + 1}-${v4()}`}
                data-qa-selector={`exportables-context-clues-excerpt-definition-input-${index + 1}`}
                aria-label="Definition input"
                css={styles.textareaStyles}
                value={excerpt.definition}
                onChange={(e) =>
                  handleExcerptChange(index, "definition", e.target.value)
                }
                maxLength={350}
              />
            </AdjustFontSizeToFitContent>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ContextCluesDetective;
